import React, { useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Calendar,
  Users,
  FileText,
  Edit,
  Eye,
  Clock,
  Award,
  Save,
  X,
  Archive,
} from "lucide-react";
import { toast } from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getTeacherExams,
  getExamGrades,
  createExam,
  updateExam,
  archiveExam,
  unarchiveExam,
  Exam,
  Grade,
  CreateExamData,
  submitExamGrades,
  GradeSubmission,
} from "../../services/exams";
import { quizService } from "../../services/quizService";
import { getMyTeacherProfile } from "../../services/teachers";

interface ExamLocal {
  _id: string;
  title: string;
  description?: string;
  type: "physical" | "online";
  quizId?: string;
  courseId: {
    _id: string;
    name: string;
    code: string;
  };
  schedule: {
    date: string;
    startTime: string;
    endTime: string;
    duration: number;
  };
  venue?: string;
  maxMarks: number;
  weightage: number;
  status: "scheduled" | "ongoing" | "completed" | "cancelled" | "archived";
  instructions?: string;
  gradeSubmissionDeadline?: string;
  createdAt: string;
}

interface GradeLocal {
  _id: string;
  studentId: {
    _id: string;
    admissionNumber: string;
    userId: {
      firstName: string;
      lastName: string;
    };
  };
  marks: number;
  maxMarks: number;
  percentage: number;
  grade: string;
  remarks?: string;
  status: string;
}

const TeacherExamsGrades: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<"exams" | "grades">("exams");
  const [selectedExam, setSelectedExam] = useState<ExamLocal | null>(null);
  const [showCreateExam, setShowCreateExam] = useState(false);
  const [showEditExam, setShowEditExam] = useState(false);
  const [editingExam, setEditingExam] = useState<ExamLocal | null>(null);
  const [showGradeEntry, setShowGradeEntry] = useState(false);
  const [showArchivedExams, setShowArchivedExams] = useState(false);
  const [gradeEntries, setGradeEntries] = useState<{
    [studentId: string]: { marks: number; remarks: string };
  }>({});

  // Exam creation form state
  const [examForm, setExamForm] = useState<CreateExamData>({
    courseId: "",
    title: "",
    description: "",
    type: "physical",
    quizId: "",
    schedule: {
      date: "",
      startTime: "",
      endTime: "",
      duration: 60,
    },
    venue: "",
    maxMarks: 100,
    weightage: 20,
    instructions: "",
    gradeSubmissionDeadline: "",
  });

  // Fetch teacher's profile to get courses
  const { data: teacherData } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: getMyTeacherProfile,
    enabled: !!user?.id,
  });

  const teacher = teacherData?.data;
  const { data: quizzesData } = useQuery({
    queryKey: ["course-quizzes", examForm.courseId],
    queryFn: () =>
      quizService.getTeacherQuizzes({ courseId: examForm.courseId }),
    enabled: !!examForm.courseId && examForm.type === "online",
  });

  const quizzes = quizzesData?.quizzes || [];

  // Fetch students for grade entry
  const { data: courseStudentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["course-students", selectedExam?.courseId._id],
    queryFn: async () => {
      // Import the students service
      const { getStudents } = await import("../../services/students");
      const result = await getStudents({
        courseId: selectedExam?.courseId._id,
        limit: 1000,
      }); // Get all students for the course
      return { students: result.data };
    },
    enabled: !!selectedExam && showGradeEntry,
  });

  const courseStudents = courseStudentsData?.students || [];

  // Create exam mutation
  const createExamMutation = useMutation({
    mutationFn: createExam,
    onSuccess: () => {
      toast.success("Exam created successfully!");
      setShowCreateExam(false);
      queryClient.invalidateQueries({ queryKey: ["teacher-exams"] });
      // Reset form
      setExamForm({
        courseId: "",
        title: "",
        description: "",
        type: "physical",
        quizId: "",
        schedule: {
          date: "",
          startTime: "",
          endTime: "",
          duration: 60,
        },
        venue: "",
        maxMarks: 100,
        weightage: 20,
        instructions: "",
        gradeSubmissionDeadline: "",
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to create exam");
    },
  });

  // Update exam mutation
  const updateExamMutation = useMutation({
    mutationFn: ({
      examId,
      updateData,
    }: {
      examId: string;
      updateData: Partial<CreateExamData>;
    }) => updateExam(examId, updateData),
    onSuccess: () => {
      toast.success("Exam updated successfully!");
      setShowEditExam(false);
      setEditingExam(null);
      queryClient.invalidateQueries({ queryKey: ["teacher-exams"] });
      // Reset form
      setExamForm({
        courseId: "",
        title: "",
        description: "",
        type: "physical",
        quizId: "",
        schedule: {
          date: "",
          startTime: "",
          endTime: "",
          duration: 60,
        },
        venue: "",
        maxMarks: 100,
        weightage: 20,
        instructions: "",
        gradeSubmissionDeadline: "",
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to update exam");
    },
  });

  // Archive exam mutation
  const archiveExamMutation = useMutation({
    mutationFn: (examId: string) => archiveExam(examId),
    onSuccess: () => {
      toast.success("Exam archived successfully!");
      queryClient.invalidateQueries({ queryKey: ["teacher-exams"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to archive exam");
    },
  });

  // Unarchive exam mutation
  const unarchiveExamMutation = useMutation({
    mutationFn: (examId: string) => unarchiveExam(examId),
    onSuccess: () => {
      toast.success("Exam unarchived successfully!");
      queryClient.invalidateQueries({ queryKey: ["teacher-exams"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to unarchive exam");
    },
  });

  const submitGradesMutation = useMutation({
    mutationFn: ({
      examId,
      grades,
    }: {
      examId: string;
      grades: GradeSubmission[];
    }) => submitExamGrades(examId, grades),
    onSuccess: () => {
      toast.success("Grades submitted successfully!");
      setShowGradeEntry(false);
      setGradeEntries({});
      queryClient.invalidateQueries({ queryKey: ["exam-grades"] });
      queryClient.invalidateQueries({ queryKey: ["teacher-exams"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to submit grades");
    },
  });

  const handleCreateExam = () => {
    if (!examForm.courseId || !examForm.title || !examForm.schedule.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    createExamMutation.mutate(examForm);
  };

  const handleEditExam = (exam: ExamLocal) => {
    setEditingExam(exam);
    setExamForm({
      courseId: exam.courseId._id,
      title: exam.title,
      description: exam.description || "",
      type: exam.type,
      quizId: exam.quizId || "",
      schedule: {
        date: exam.schedule.date,
        startTime: exam.schedule.startTime,
        endTime: exam.schedule.endTime,
        duration: exam.schedule.duration,
      },
      venue: exam.venue || "",
      maxMarks: exam.maxMarks,
      weightage: exam.weightage,
      instructions: exam.instructions || "",
      gradeSubmissionDeadline: exam.gradeSubmissionDeadline || "",
    });
    setShowEditExam(true);
  };

  const handleArchiveExam = (exam: ExamLocal) => {
    if (
      window.confirm(
        `Are you sure you want to archive "${exam.title}"? This action cannot be undone.`
      )
    ) {
      archiveExamMutation.mutate(exam._id);
    }
  };

  const handleUnarchiveExam = (exam: ExamLocal) => {
    if (window.confirm(`Are you sure you want to unarchive "${exam.title}"?`)) {
      unarchiveExamMutation.mutate(exam._id);
    }
  };

  const handleUpdateExam = () => {
    if (
      !editingExam ||
      !examForm.courseId ||
      !examForm.title ||
      !examForm.schedule.date
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    updateExamMutation.mutate({
      examId: editingExam._id,
      updateData: examForm,
    });
  };

  const handleFormChange = (field: string, value: any) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setExamForm((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof CreateExamData] as any),
          [child]: value,
        },
      }));
    } else {
      setExamForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleGradeEntryChange = (
    studentId: string,
    field: "marks" | "remarks",
    value: string | number
  ) => {
    setGradeEntries((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  const handleSubmitGrades = () => {
    if (!selectedExam) return;

    const grades: GradeSubmission[] = courseStudents
      .filter((student: any) => gradeEntries[student._id])
      .map((student: any) => ({
        studentId: student._id,
        marks: gradeEntries[student._id].marks,
        remarks: gradeEntries[student._id].remarks || "",
      }));

    if (grades.length === 0) {
      toast.error("Please enter grades for at least one student");
      return;
    }

    submitGradesMutation.mutate({
      examId: selectedExam._id,
      grades,
    });
  };

  // Fetch teacher's exams
  const {
    data: examsData,
    isLoading: examsLoading,
    error: examsError,
  } = useQuery({
    queryKey: ["teacher-exams"],
    queryFn: getTeacherExams,
    enabled: !!user?.id,
  });

  // Fetch grades for selected exam
  const { data: gradesData, isLoading: gradesLoading } = useQuery({
    queryKey: ["exam-grades", selectedExam?._id],
    queryFn: () => getExamGrades(selectedExam!._id),
    enabled: !!selectedExam?._id,
  });

  const exams = examsData || [];
  const activeExams = exams.filter((exam: Exam) => exam.status !== "archived");
  const archivedExams = exams.filter(
    (exam: Exam) => exam.status === "archived"
  );
  const grades = gradesData || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-gradient-to-br from-blue-300 to-indigo-400 text-white";
      case "ongoing":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white";
      case "completed":
        return "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white";
      case "cancelled":
        return "bg-gradient-to-br from-red-300 to-pink-400 text-white";
      case "archived":
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white";
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case "Distinction":
        return "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white";
      case "Credit":
        return "bg-gradient-to-br from-blue-300 to-indigo-400 text-white";
      case "Pass":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white";
      case "Fail":
        return "bg-gradient-to-br from-red-300 to-pink-400 text-white";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewGrades = (exam: Exam) => {
    setSelectedExam(exam as unknown as ExamLocal);
    setActiveTab("grades");
  };

  const handleEnterGrades = (exam: Exam) => {
    setSelectedExam(exam as unknown as ExamLocal);
    setShowGradeEntry(true);
  };

  if (examsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading exams...</p>
        </div>
      </div>
    );
  }

  if (examsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 max-w-md">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-slate-900 font-bold text-center text-lg">
            Error Loading Exams
          </h3>
          <p className="text-slate-600 font-medium mt-2 text-center">
            Unable to load exam data. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="mb-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h1 className="text-2xl font-bold text-slate-900">Exams & Grades</h1>
        <p className="text-slate-600 mt-1 font-medium">
          Manage examinations and student grades for your courses
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-4">
        <div className="border-b border-slate-200/60">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("exams")}
              className={`py-2 px-1 border-b-2 font-bold text-sm transition-all ${
                activeTab === "exams"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <Calendar className="inline-block w-4 h-4 mr-2" />
              My Exams
            </button>
            <button
              onClick={() => setActiveTab("grades")}
              className={`py-2 px-1 border-b-2 font-bold text-sm transition-all ${
                activeTab === "grades"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <Award className="inline-block w-4 h-4 mr-2" />
              Grade Management
            </button>
          </nav>
        </div>
      </div>

      {/* Exams Tab */}
      {activeTab === "exams" && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-900">My Exams</h2>
            <div className="flex space-x-2">
              {archivedExams.length > 0 && (
                <button
                  onClick={() => setShowArchivedExams(true)}
                  className="bg-gradient-to-br from-slate-300 to-slate-400 text-white px-4 py-2.5 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center font-bold"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archived Exams ({archivedExams.length})
                </button>
              )}
              <button
                onClick={() => setShowCreateExam(true)}
                className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-4 py-2.5 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center font-bold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Exam
              </button>
            </div>
          </div>

          {activeExams.length === 0 ? (
            <div className="text-center py-16 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto shadow-lg">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="mt-6 text-lg font-bold text-slate-900">
                No exams scheduled
              </h3>
              <p className="mt-2 text-slate-600 font-medium max-w-md mx-auto">
                Get started by creating your first exam.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateExam(true)}
                  className="inline-flex items-center px-6 py-2.5 bg-gradient-to-br from-blue-300 to-indigo-400 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Exam
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activeExams.map((exam: Exam) => (
                <div
                  key={exam._id}
                  className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6 hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">
                        {exam.title}
                      </h3>
                      <p className="text-sm text-slate-600 font-medium mt-1">
                        {exam.courseId.code} - {exam.courseId.name}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold shadow-sm ${getStatusColor(
                        exam.status
                      )}`}
                    >
                      {exam.status}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-slate-600 font-medium">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mr-2 shadow-sm">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      {formatDate(exam.schedule.date)}
                    </div>
                    <div className="flex items-center text-sm text-slate-600 font-medium">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center mr-2 shadow-sm">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      {exam.schedule.startTime} - {exam.schedule.endTime}
                    </div>
                    <div className="flex items-center text-sm text-slate-600 font-medium">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mr-2 shadow-sm">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      {exam.type === "online" ? "Online Quiz" : "Physical Exam"}
                    </div>
                    <div className="flex items-center text-sm text-slate-600 font-medium">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mr-2 shadow-sm">
                        <Award className="w-4 h-4 text-white" />
                      </div>
                      Max Marks: {exam.maxMarks}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col space-y-2">
                    {exam.status !== "completed" &&
                      exam.status !== "ongoing" &&
                      exam.status !== "archived" && (
                        <button
                          onClick={() => handleEditExam(exam)}
                          className="w-full bg-gradient-to-br from-amber-300 to-yellow-400 text-white px-3 py-2 rounded-xl text-sm font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit Exam
                        </button>
                      )}
                    <button
                      onClick={() => handleViewGrades(exam)}
                      className="w-full bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-xl text-sm font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Grades
                    </button>
                    {exam.status === "completed" && (
                      <button
                        onClick={() => handleArchiveExam(exam)}
                        className="w-full bg-gradient-to-br from-red-300 to-pink-400 text-white px-3 py-2 rounded-xl text-sm font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        disabled={archiveExamMutation.isPending}
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        {archiveExamMutation.isPending
                          ? "Archiving..."
                          : "Archive"}
                      </button>
                    )}
                    {exam.type === "physical" &&
                      exam.status !== "completed" &&
                      exam.status !== "archived" && (
                        <button
                          onClick={() => handleEnterGrades(exam)}
                          className="w-full bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-3 py-2 rounded-xl text-sm font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Enter Grades
                        </button>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grades Tab */}
      {activeTab === "grades" && (
        <div>
          {selectedExam ? (
            <div>
              <div className="mb-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
                <button
                  onClick={() => {
                    setSelectedExam(null);
                    setActiveTab("exams");
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-bold mb-4"
                >
                  ← Back to Exams
                </button>
                <h2 className="text-xl font-bold text-slate-900 mt-2">
                  Grades for: {selectedExam.title}
                </h2>
                <p className="text-slate-600 font-medium">
                  {selectedExam.courseId.code} - {selectedExam.courseId.name}
                </p>
              </div>

              {gradesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
                </div>
              ) : grades.length === 0 ? (
                <div className="text-center py-16 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto shadow-lg">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="mt-6 text-lg font-bold text-slate-900">
                    No grades submitted yet
                  </h3>
                  <p className="mt-2 text-slate-600 font-medium max-w-md mx-auto">
                    Grades will appear here once students complete the exam.
                  </p>
                </div>
              ) : (
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Marks
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Percentage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Grade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {grades.map((grade: Grade) => (
                        <tr
                          key={grade._id}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-slate-900">
                              {(grade as unknown as GradeLocal).studentId
                                ?.userId?.firstName || "Unknown"}{" "}
                              {(grade as unknown as GradeLocal).studentId
                                ?.userId?.lastName || ""}
                            </div>
                            <div className="text-sm text-slate-600 font-medium">
                              ({" "}
                              {(grade as unknown as GradeLocal).studentId
                                ?.admissionNumber || "N/A"}
                              )
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                            {grade.marks}/{grade.maxMarks}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                            {grade.percentage.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold shadow-sm ${getGradeColor(
                                grade.grade
                              )}`}
                            >
                              {grade.grade}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                            {grade.remarks || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto shadow-lg">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h3 className="mt-6 text-lg font-bold text-slate-900">
                Select an exam to view grades
              </h3>
              <p className="mt-2 text-slate-600 font-medium max-w-md mx-auto">
                Choose an exam from the "My Exams" tab to view student grades.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Exam Modal */}
      {(showCreateExam || showEditExam) && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border border-slate-200/60 w-full max-w-2xl shadow-xl rounded-2xl bg-white/95 backdrop-blur-xl max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-900">
                  {showEditExam ? "Edit Exam" : "Create New Exam"}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateExam(false);
                    setShowEditExam(false);
                    setEditingExam(null);
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-1">
                      Course *
                    </label>
                    <select
                      value={examForm.courseId}
                      onChange={(e) =>
                        handleFormChange("courseId", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium bg-white shadow-sm"
                      required
                    >
                      <option value="">Select a course</option>
                      {teacher?.currentClasses?.flatMap(
                        (classItem: any) =>
                          classItem.courses?.map((course: any) => (
                            <option key={course._id} value={course._id}>
                              {course.name}({course.level}) -{" "}
                              {classItem.classId.name}
                            </option>
                          )) || []
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-1">
                      Exam Type *
                    </label>
                    <select
                      value={examForm.type}
                      onChange={(e) => handleFormChange("type", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="physical">Physical Exam</option>
                      <option value="online">Online Quiz</option>
                    </select>
                  </div>

                  {examForm.type === "online" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Quiz *
                      </label>
                      <select
                        value={examForm.quizId}
                        onChange={(e) =>
                          handleFormChange("quizId", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={examForm.type === "online"}
                      >
                        <option value="">Select a quiz</option>
                        {quizzes.map((quiz: any) => (
                          <option key={quiz._id} value={quiz._id}>
                            {quiz.title}
                          </option>
                        ))}
                      </select>
                      {quizzes.length === 0 && examForm.courseId && (
                        <p className="text-sm text-gray-500 mt-1">
                          No quizzes available for this course. Please create a
                          quiz first.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exam Title *
                  </label>
                  <input
                    type="text"
                    value={examForm.title}
                    onChange={(e) => handleFormChange("title", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Mid-term Examination"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={examForm.description}
                    onChange={(e) =>
                      handleFormChange("description", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional description of the exam"
                  />
                </div>

                {/* Schedule */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Schedule
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={examForm.schedule.date}
                        onChange={(e) =>
                          handleFormChange("schedule.date", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (minutes) *
                      </label>
                      <input
                        type="number"
                        value={examForm.schedule.duration}
                        onChange={(e) =>
                          handleFormChange(
                            "schedule.duration",
                            parseInt(e.target.value)
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={examForm.schedule.startTime}
                        onChange={(e) =>
                          handleFormChange("schedule.startTime", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time *
                      </label>
                      <input
                        type="time"
                        value={examForm.schedule.endTime}
                        onChange={(e) =>
                          handleFormChange("schedule.endTime", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Exam Details */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Exam Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Marks *
                      </label>
                      <input
                        type="number"
                        value={examForm.maxMarks}
                        onChange={(e) =>
                          handleFormChange("maxMarks", parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Weightage (%) *
                      </label>
                      <input
                        type="number"
                        value={examForm.weightage}
                        onChange={(e) =>
                          handleFormChange(
                            "weightage",
                            parseInt(e.target.value)
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        max="100"
                        required
                      />
                    </div>

                    {examForm.type === "physical" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Venue *
                          </label>
                          <input
                            type="text"
                            value={examForm.venue}
                            onChange={(e) =>
                              handleFormChange("venue", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Room 101"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Grade Submission Deadline *
                          </label>
                          <input
                            type="datetime-local"
                            value={examForm.gradeSubmissionDeadline}
                            onChange={(e) =>
                              handleFormChange(
                                "gradeSubmissionDeadline",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={examForm.instructions}
                    onChange={(e) =>
                      handleFormChange("instructions", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Special instructions for students"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateExam(false);
                      setShowEditExam(false);
                      setEditingExam(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={showEditExam ? handleUpdateExam : handleCreateExam}
                    disabled={
                      createExamMutation.isPending ||
                      updateExamMutation.isPending
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {createExamMutation.isPending ||
                    updateExamMutation.isPending ? (
                      <>
                        <LoadingSpinner />
                        <span className="ml-2">
                          {showEditExam ? "Updating..." : "Creating..."}
                        </span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {showEditExam ? "Update Exam" : "Create Exam"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Grade Entry Modal */}
      {showGradeEntry && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Enter Grades - {selectedExam?.title}
                </h3>
                <button
                  onClick={() => setShowGradeEntry(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {studentsLoading ? (
                <LoadingSpinner />
              ) : courseStudents.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No students found for this course.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Course: {selectedExam?.courseId.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Maximum Marks: {selectedExam?.maxMarks}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Admission No.
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Marks
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Remarks
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {courseStudents.map((student: any) => (
                          <tr key={student._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {student.userInfo.firstName}{" "}
                                {student.userInfo.lastName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {student.admissionNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                min="0"
                                max={selectedExam?.maxMarks}
                                value={gradeEntries[student._id]?.marks || ""}
                                onChange={(e) =>
                                  handleGradeEntryChange(
                                    student._id,
                                    "marks",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                value={gradeEntries[student._id]?.remarks || ""}
                                onChange={(e) =>
                                  handleGradeEntryChange(
                                    student._id,
                                    "remarks",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Optional remarks"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      onClick={() => setShowGradeEntry(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitGrades}
                      disabled={submitGradesMutation.isPending}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {submitGradesMutation.isPending ? (
                        <>
                          <LoadingSpinner />
                          <span className="ml-2">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Submit Grades
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archived Exams Modal */}
      {showArchivedExams && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Archived Exams
              </h3>
              <button
                onClick={() => setShowArchivedExams(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {archivedExams.length === 0 ? (
              <div className="text-center py-12">
                <Archive className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No archived exams
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Archived exams will appear here.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {archivedExams.map((exam: Exam) => (
                  <div
                    key={exam._id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {exam.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {exam.courseId.code} - {exam.courseId.name}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Archived
                      </span>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(exam.schedule.date)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        {exam.schedule.startTime} - {exam.schedule.endTime}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FileText className="w-4 h-4 mr-2" />
                        {exam.type === "online"
                          ? "Online Quiz"
                          : "Physical Exam"}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Award className="w-4 h-4 mr-2" />
                        Max Marks: {exam.maxMarks}
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-2">
                      <button
                        onClick={() => handleViewGrades(exam)}
                        className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-200 flex items-center justify-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Grades
                      </button>
                      <button
                        onClick={() => handleUnarchiveExam(exam)}
                        className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-green-700 flex items-center justify-center"
                        disabled={archiveExamMutation.isPending}
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        {archiveExamMutation.isPending
                          ? "Unarchiving..."
                          : "Unarchive"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherExamsGrades;
